import { PropsWithChildren, useMemo, useState } from 'react';
import { ControlledMenu } from '@szhsin/react-menu';
import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  usePinnedRfqsAction,
  usePinnedRfqsView,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/pinned-rfqs-context';
import useCreateMarket from '@paradigm/features/src/unified-rfqs/ui/hooks/useCreateMarket';

import { Grfq } from '@paradigm/features/src/unified-rfqs/entities/grfq/domain';
import { toast, TOAST_TIMEOUT_LONG } from '#/components/UnifiedRfqs/toast';
import { Maybe } from '@paradigm/utils/types';
import { useTrackAction } from '@paradigm/features/src/analytics';
import type { AnchorPoint } from '#/components/context-menu';
import type { Drfq } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';

interface OpenContextMenuParams<DataType = unknown> {
  readonly anchorPoint: AnchorPoint;
  readonly rfqId: Drfq['id'] | Grfq['id'];
  readonly detailsItemId?: number | string;
  readonly account?: Maybe<string>;
  readonly data?: DataType;
  readonly productCode?: Maybe<string>;
}

interface Actions {
  readonly open: <DataType>(params: OpenContextMenuParams<DataType>) => void;
  readonly close: () => void;
  readonly createRfq: (type: 'OB' | 'RFQ') => void;
  readonly openDetailsModal: () => void;
  readonly closeDetailsModal: () => void;
  readonly togglePinnedRfq: () => void;
}

interface State {
  readonly rfqId?: Drfq['id'] | Grfq['id'] | null;
  readonly productCode?: Maybe<string>;
  readonly detailsItemId: number | string | null;
  readonly anchorPoint?: AnchorPoint;
  readonly isItemDetailsOpen: boolean;
  readonly isPinnedRfq: boolean;
  readonly data?: unknown;
}

interface View<DataType> extends Omit<State, 'data'> {
  readonly data: Maybe<DataType>;
}

const [useStateCtx, StateCtxProvider] = createSafeContext<State>(
  'RfqsContextMenuState',
);

const [useActionsCtx, ActionsCtxProvider] = createSafeContext<Actions>(
  'RfqsContextMenuActions',
);

export const useContextMenuActions = useActionsCtx;

export default function ContextMenuProvider({ children }: PropsWithChildren) {
  const [anchorPoint, setAnchorPoint] = useState<AnchorPoint>();
  const [rfqId, setRfqId] = useState<Drfq['id'] | Grfq['id'] | null>(null);
  const [productCode, setProductCode] = useState<Maybe<string>>();
  const [prefillAccount, setPrefillAccount] = useState<Maybe<string>>();
  const [detailsItemId, setDetailsItemId] = useState<string | number | null>(
    null,
  );
  const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(false);
  const track = useTrackAction();
  const { togglePinRfq } = usePinnedRfqsAction();
  const { isPinned } = usePinnedRfqsView();
  const { createMarket } = useCreateMarket();
  const [data, setData] = useState<unknown>();

  function open<DataType>(args: OpenContextMenuParams) {
    setAnchorPoint(args.anchorPoint);
    setRfqId(args.rfqId);
    setDetailsItemId(args.detailsItemId ?? null);
    setPrefillAccount(args.account);
    setProductCode(args.productCode);
    setData(args.data as DataType);
  }

  const actions = useMemo<Actions>(
    () => ({
      open,
      close: () => setAnchorPoint(undefined),
      createRfq: (type) => {
        track('create market', { auctionType: type, isContextMenu: true });
        createMarket({
          rfqId,
          type,
          prefillAccount,
          onError: (message: string) =>
            toast(message, { autoClose: TOAST_TIMEOUT_LONG }),
        });
      },
      openDetailsModal: () => {
        track('view rfq details');
        setIsItemDetailsOpen(true);
      },
      closeDetailsModal: () => {
        setIsItemDetailsOpen(false);
        setDetailsItemId(null);
      },
      togglePinnedRfq: () => {
        if (rfqId == null) return;
        track('pin/unpin market row');
        togglePinRfq(rfqId);
      },
    }),
    [track, createMarket, rfqId, prefillAccount, togglePinRfq],
  );

  const view = useMemo(
    () => ({
      rfqId,
      anchorPoint,
      detailsItemId,
      productCode,
      isItemDetailsOpen,
      isPinnedRfq: rfqId != null && isPinned(rfqId),
      data,
    }),
    [
      rfqId,
      anchorPoint,
      detailsItemId,
      productCode,
      isItemDetailsOpen,
      isPinned,
      data,
    ],
  );

  return (
    <ActionsCtxProvider value={actions}>
      <StateCtxProvider value={view}>{children}</StateCtxProvider>
    </ActionsCtxProvider>
  );
}

export function ContextMenu({ children }: PropsWithChildren) {
  const { anchorPoint } = useStateCtx();
  const contextMenu = useActionsCtx();

  return (
    <ControlledMenu
      anchorPoint={anchorPoint}
      state={anchorPoint != null ? 'opening' : 'closing'}
      onClose={contextMenu.close}
    >
      {anchorPoint != null && children}
    </ControlledMenu>
  );
}

export function useContextMenuView<DataType = unknown>(): View<DataType> {
  const ctx = useStateCtx();
  return { ...ctx, data: ctx.data as Maybe<DataType> };
}
